import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { newPasswordSchema } from '@lims/shared-validation';
import { ActionButton, Alert, ErrorState, Form, TextField } from '../components/ui/index.js';
import { portalApi } from './portal-api.js';
import { PortalPublicLayout } from './PortalShell.js';
import { usePortalSession } from './PortalSession.js';

const linkClass = 'rounded-sm text-center text-body text-primary-800 hover:underline';

/**
 * Portal sign-in, with "forgot password" on the same page.
 *
 * A reset request is ALWAYS answered the same way: the page never says
 * whether an address has an account, exactly as the server never does.
 */
export function PortalLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, signIn } = usePortalSession();

  const [mode, setMode] = useState<'signIn' | 'forgot'>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);

  if (me) return <Navigate to="/portal" replace />;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signIn') {
        await signIn(email.trim(), password);
        navigate('/portal', { replace: true });
      } else {
        await portalApi.requestPasswordReset(email.trim());
        setResetRequested(true);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next: 'signIn' | 'forgot') => {
    setMode(next);
    setError(null);
    setResetRequested(false);
  };

  return (
    <PortalPublicLayout
      title={mode === 'signIn' ? t('portal.login.title') : t('portal.login.forgotTitle')}
      subtitle={mode === 'signIn' ? t('portal.login.subtitle') : t('portal.login.forgotSubtitle')}
    >
      {mode === 'forgot' && resetRequested ? (
        <Alert tone="info" title={t('portal.login.resetRequested')} />
      ) : (
        <Form onSubmit={submit} isSubmitting={isSubmitting}>
          <TextField
            type="email"
            label={t('portal.login.email')}
            value={email}
            onChange={setEmail}
            autoComplete="email"
            isRequired
          />
          {mode === 'signIn' ? (
            <TextField
              type="password"
              label={t('portal.login.password')}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              isRequired
            />
          ) : null}
          {error ? <ErrorState error={error} /> : null}
          <ActionButton
            priority="primary"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={!email.trim() || (mode === 'signIn' && !password)}
            fullWidth
          >
            {mode === 'signIn' ? t('portal.login.submit') : t('portal.login.forgotSubmit')}
          </ActionButton>
        </Form>
      )}
      <button
        type="button"
        className={linkClass}
        onClick={() => switchMode(mode === 'signIn' ? 'forgot' : 'signIn')}
      >
        {mode === 'signIn' ? t('portal.login.forgotLink') : t('portal.login.backToSignIn')}
      </button>
    </PortalPublicLayout>
  );
}

/** Set a password from the sign-in provider's link (an invitation or a reset). */
export function PortalResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  // The provider returns its token in the URL fragment; a query parameter is
  // accepted too, as on the staff page.
  const fragment = new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, ''),
  );
  const token = params.get('token') ?? fragment.get('access_token') ?? fragment.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isDone, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    setLocalError(null);
    if (password !== confirmation) {
      setLocalError(t('auth.passwordMismatch'));
      return;
    }
    if (!newPasswordSchema.safeParse(password).success) {
      setLocalError(t('auth.passwordRules'));
      return;
    }
    setSubmitting(true);
    try {
      await portalApi.confirmPasswordReset(token, password);
      setDone(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  const back = (
    <Link to="/portal/login" className={linkClass}>
      {t('portal.login.backToSignIn')}
    </Link>
  );

  return (
    <PortalPublicLayout title={t('portal.reset.title')} subtitle={t('portal.reset.subtitle')}>
      {isDone ? (
        <>
          <Alert tone="success" title={t('portal.reset.done')} />
          {back}
        </>
      ) : !token ? (
        <>
          <Alert tone="warning" title={t('portal.reset.missingToken')} />
          {back}
        </>
      ) : (
        <Form onSubmit={submit} isSubmitting={isSubmitting}>
          <TextField
            type="password"
            label={t('auth.newPassword')}
            description={t('auth.passwordRules')}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            isRequired
          />
          <TextField
            type="password"
            label={t('auth.confirmPassword')}
            value={confirmation}
            onChange={setConfirmation}
            autoComplete="new-password"
            isRequired
            {...(localError ? { error: localError } : {})}
          />
          {error ? <ErrorState error={error} /> : null}
          <ActionButton
            priority="primary"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={!password || !confirmation}
            fullWidth
          >
            {t('portal.reset.submit')}
          </ActionButton>
        </Form>
      )}
    </PortalPublicLayout>
  );
}
